import { createApi } from '@reduxjs/toolkit/query/react';

import {
  PurchaseDocumentRequest,
  PurchaseDocumentResponse,
  PurchaseFormResponse,
  PurchaseItemResponse,
  TenderCreationResponse,
  TenderCreationRequest,
} from '@Types/api/form';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import { customBaseQuery } from '@Utils/api/customBaseQuery';

export const purchaseRequestApi = createApi({
  tagTypes: ['purchaseRequest', 'purchaseRequestItem'],
  reducerPath: ApiReducerPath.API_REQUISITION_PURCHASE_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getSearchListPurchaseRequests: builder.mutation<
      RequestListSearchResponse<PurchaseFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'reqId',
        sortOrder = 'desc',
        searchValue,
        filters = [],
      }) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/search`,
        method: 'post',
        body: {
          searchValue: searchValue,
          searchCriteriaList: [
            {
              searchCriterias: filters,
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getPurchaseRequest: builder.query<PurchaseFormResponse, string>({
      query: (id) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/${id}`,
        method: 'get',
      }),
    }),
    createTender: builder.mutation<
      TenderCreationResponse,
      TenderCreationRequest
    >({
      query: ({ reqItemIds = [] }) => ({
        url: `${ApiBaseName.REQUISITION}/tender`,
        method: 'post',
        body: {
          reqItemIds,
        },
      }),
    }),
    submitTender: builder.mutation<string, number>({
      query: (requestId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/submit`,
        method: 'post',
        params: { requestId },
      }),
    }),
    deletePurchaseRequest: builder.mutation<null, string | number>({
      query: (requestId) => ({
        url: `${ApiBaseName.REQUISITION}/purchase`,
        method: 'delete',
        params: { requestId },
      }),
    }),
    getPurchaseItems: builder.mutation<Array<PurchaseItemResponse>, number>({
      query: (requestId) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/item/${requestId}`,
        method: 'get',
      }),
    }),

    getPurchaseDocument: builder.query<Array<PurchaseDocumentResponse>, string>(
      {
        query: (purchaseId) => ({
          url: `${ApiBaseName.REQUISITION}/purchase/${purchaseId}/document`,
          method: 'get',
        }),
      }
    ),
    postPurchaseDocument: builder.mutation<
      PurchaseDocumentResponse,
      { body: PurchaseDocumentRequest; purchaseId: string | number }
    >({
      query: ({ body, purchaseId }) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/${purchaseId}/document`,
        method: 'post',
        body,
      }),
    }),

    deletePurchaseDocument: builder.mutation<null, string | number>({
      query: (purchaseId) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/${purchaseId}/document`,
        method: 'delete',
      }),
    }),

    importPurchaseRequest: builder.mutation<string, string>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/import`,
        method: 'post',
        body,
      }),
    }),

    exportPurchaseRequest: builder.mutation<
      Blob,
      {
        requestIds: Array<string | number>;
      }
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/purchase/download`,
        method: 'post',
        body,
      }),
    }),
  }),
});

export const {
  useDeletePurchaseDocumentMutation,
  useDeletePurchaseRequestMutation,
  useExportPurchaseRequestMutation,
  useGetPurchaseDocumentQuery,
  useGetPurchaseRequestQuery,
  useGetSearchListPurchaseRequestsMutation,
  useImportPurchaseRequestMutation,
  usePostPurchaseDocumentMutation,
  useCreateTenderMutation,
  useSubmitTenderMutation,
  useGetPurchaseItemsMutation,
} = purchaseRequestApi;
